import React from "react";
import PanelShell from "../shared/PanelShell";
import HoverTooltip from "../shared/HoverTooltip";
import { fmtNumber } from "../shared/format";
import { groupBarriers, GROUPS, yoursToFixCount } from "./lib/whoFixes";

// BARRIER GROUPS — Tier 3 (V6 §E.6). Why calls didn't convert, grouped under three
// who_fixes headings, because "the grouping is the entire point — an owner needs to
// know which of these are theirs". A flat ranked bar list is the wrong form (it
// hides the grouping); this extends the ActionList row pattern with group headings.
//
// RENDER label, NEVER barrier. `label` is the owner-facing wording ("Agent got it
// wrong"); `barrier` is the storage key ("agent_error"). `what_it_means` goes in a
// hover tooltip, not on the row. The grouping (who_fixes → heading) lives in
// lib/whoFixes.js and follows V6 §E.6 (knowledge_base/agent_config → We can fix;
// property_decision → You can fix; platform/guest_side → Nobody can).
//
// Each row carries a drill object; clicking it opens the DrillOver for that barrier
// (onRowClick(row.drill) → fn_drill('barrier', drill.id, scope)). A row with no
// drill is not a control. The bars are proportional to the largest count on the
// panel — a presentation detail, not a derived metric.
//
// The bar's colour carries the C.2 treatment of its group: the property's own
// barriers leak (they are where bookings are lost and the owner can act), ours-to-fix
// reads as actionable, nobody-can is inert.

const GROUP_BAR = {
  [GROUPS.YOURS]: "bg-red-400 dark:bg-red-500",
  [GROUPS.OURS]: "bg-amber-400 dark:bg-amber-500",
  [GROUPS.NOBODY]: "bg-gray-300 dark:bg-gray-600",
};
const UNKNOWN_BAR = "bg-gray-300 dark:bg-gray-600";

const InfoGlyph = () => (
  <svg className="w-3.5 h-3.5 shrink-0 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const BarrierRow = ({ row, max, barClass, onRowClick }) => {
  const count = Number(row.count ?? 0);
  const pct = max > 0 ? Math.max(2, Math.round((count / max) * 100)) : 0;
  const drillable = !!onRowClick && !!row.drill;

  const inner = (
    <>
      <span className="flex items-center gap-1.5 min-w-0">
        <span className="gi-body text-gray-900 dark:text-gray-100 truncate">{row.label}</span>
        {row.what_it_means && (
          <HoverTooltip content={<span className="gi-body">{row.what_it_means}</span>}>
            <InfoGlyph />
          </HoverTooltip>
        )}
      </span>
      <span className="flex items-center gap-2 shrink-0">
        <span className="hidden sm:block h-2 w-24 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden" aria-hidden="true">
          <span className={`block h-full rounded-full ${barClass}`} style={{ width: `${pct}%` }} />
        </span>
        <span className="gi-body font-semibold tabular-nums text-gray-900 dark:text-gray-100 w-6 text-right">
          {fmtNumber(count)}
        </span>
      </span>
    </>
  );

  if (!drillable) {
    return (
      <div data-barrier={row.barrier} className="flex items-center justify-between gap-3 px-2 py-2 even:bg-gray-50 dark:even:bg-gray-800/40">
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      data-barrier={row.barrier}
      onClick={() => onRowClick(row.drill)}
      className="w-full flex items-center justify-between gap-3 px-2 py-2 min-h-[44px] text-left even:bg-gray-50 dark:even:bg-gray-800/40 hover:bg-gray-900/5 dark:hover:bg-white/5 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
    >
      {inner}
    </button>
  );
};

const BarrierGroups = ({ panelKey = "voice_barriers", label, say, barriers, onRowClick, loading = false }) => {
  const groups = groupBarriers(barriers);
  const max = (Array.isArray(barriers) ? barriers : [])
    .reduce((m, r) => Math.max(m, Number(r?.count ?? 0)), 0);

  // Summary line above the grouped list (Markup #10, §E.6): of all the barrier
  // occurrences, how many are the property's own to fix. yoursToFixCount is null
  // when none are, so the line is omitted rather than reading "0 of N are yours".
  const yours = yoursToFixCount(barriers);
  const total = (Array.isArray(barriers) ? barriers : [])
    .reduce((s, r) => s + (Number(r?.count) || 0), 0);

  // "Why calls didn't convert" on Phone, "Why conversations didn't convert" on Chat
  // — chat visitors do not call (Markup #9). An explicit label prop still overrides;
  // AnalysisColumn passes none, so panelKey (voice_barriers / chat_barriers) drives it.
  const heading = label ?? (String(panelKey).startsWith("chat")
    ? "Why conversations didn't convert"
    : "Why calls didn't convert");

  return (
    <PanelShell
      panelKey={panelKey}
      label={heading}
      say={say}
      hasData={groups.length > 0}
      loading={loading}
    >
      <div data-testid="barrier-groups" className="space-y-4">
        {yours != null && (
          <p data-testid="barriers-summary" className="gi-body text-gray-700 dark:text-gray-300">
            <span className="font-semibold text-gray-900 dark:text-gray-100">{fmtNumber(yours)} of {fmtNumber(total)}</span> are yours to fix.
          </p>
        )}
        {groups.map((group) => (
          <div key={group.heading} data-group={group.heading}>
            <h4 className="gi-label text-[#0B6FB0] dark:text-[#7FD0FF] mb-1">{group.heading}</h4>
            <div className="rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
              {group.rows.map((row, i) => (
                <BarrierRow
                  key={row.barrier || i}
                  row={row}
                  max={max}
                  barClass={group.unknown ? UNKNOWN_BAR : (GROUP_BAR[group.heading] || UNKNOWN_BAR)}
                  onRowClick={onRowClick}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </PanelShell>
  );
};

export default BarrierGroups;
